import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { RootState } from '../../app/store';
import { LOADING_STATUS } from '../../common/constants';
import {
  InitialPaginationResponse,
  PaginationParameters,
  PaginationResponse,
  PopulatePagination,
} from '../../common/types/pagination.types';
import {
  deleteFeedback,
  doSendFeedbackToCaretaker,
  doSendFeedbackToCleaner,
  doSendFeedbackToExternalEmail,
  doSendFeedbackToOwner,
  fetchFeedbackDetails,
  fetchFeedbackDetailsAsPdf,
  fetchFeedbacks,
  fetchPendingFeedbacksCount,
  updateFeedback,
  updateFeedbackSentToCleaner,
  updateFeedbackSentToExternalEmailAddress,
  updateFeedbackSentToMaintainer,
  updateFeedbackSentToOwner,
} from '../../common/api/feedBackService';
import {
  convertJsDateToApiDateFinnishTimeString,
  formatDateToFinnish,
} from '../../common/utils/dateUtils';

export enum CustomerFeedBackListOrderByDirectionOptions {
  Ascending,
  Descending,
}
export enum CustomerFeedBackListOrderByOptions {
  Random = 0,
  BookingNumber = 1,
  AccommodationNumber = 2,
  AnswerTime = 3,
  SentTime = 4,
  Rating = 5,
  CustomerLastName = 6,
  FeedbackState = 7,
  OwnerLastName = 8,
  BookingStartTime = 9,
  FeedbackHandler = 10,
}
export interface OwnerCleanerCaretakerFeedbackEmailReplyDto {
  feedbackId: string;
  freeText: string;
}
export interface ExternalEmailFeedbackEmailReplyDto {
  feedbackId: string;
  freeText: string;
  emailAddress: string;
}
export type FeedbackState =
  | 'New'
  | 'InProgress'
  | 'Approved'
  | 'Rejected'
  | 'WaitingForAnswer'
  | 'PartiallyApproved';

export const validFeedbackStates: FeedbackState[] = [
  'New',
  'InProgress',
  'Approved',
  'Rejected',
  'WaitingForAnswer',
  'PartiallyApproved',
];

export const isValidFeedbackState = (
  value: string | null,
): value is FeedbackState =>
  value !== null && validFeedbackStates.includes(value as FeedbackState);
export interface FeedbacksFilter extends PaginationParameters {
  feedbackState?: FeedbackState | null;
  ownerId: string;
  accommodationId: string;
  orderBy: CustomerFeedBackListOrderByOptions;
  FeedbackFetchAnswerStartDate: Date;
  FeedbackFetchAnswerEndDate: Date;
  OrderDirection: CustomerFeedBackListOrderByDirectionOptions;
  handlerName: string;
}

export interface FeedbackQuestion {
  id: string;
  answerPossible: boolean;
  approvable: boolean;
  modifiable: boolean;
  questionId: number;
  text: string;
  type: 'RATING' | 'TEXT' | 'SELECTION' | 'SCORE';
  serviceQuestion: boolean;
  ownerQuestion: boolean;
}

export interface FeedbackAnswer {
  id: string;
  indexNumber?: number;
  numbericValue?: number;
  originalNumbericValue?: number;
  textValue?: string;
  originalTextValue?: string;
  approved?: boolean;
  stateModified?: string;
  reply?: string;
  question: FeedbackQuestion;
}
export interface OwnerCaretakerCleanerEmail {
  emailAddress: string;
  isBookingNoteEmail: boolean;
  isPrimary: boolean;
}
export interface FeedbackDetails {
  id?: string;
  isDeleted?: boolean;
  sentTime: string;
  answerTime?: string;
  dateOfPublication?: string;
  feedbackState: string;
  contactRequested: boolean;
  contactEmail?: string;
  contactExplanation?: string;
  customerFirstName: string;
  customerLastName: string;
  customerNumber: string;
  customerEmail: string;
  accommodationNumber: string;
  accommodationName: string;
  accommodationImageUrl: string;
  bookingStartTime: string;
  bookingEndTime: string;
  bookingNumber: string;
  ownerFirstName: string;
  ownerLastName: string;
  ownerNumber: string;
  siteId: string;
  locale: string;
  approvedRejectedTime?: string;
  sentToOwner?: string;
  sentToMaintainer?: string;
  sentToCleaner?: string;
  answers: Array<FeedbackAnswer>;
  serviceComment: string;
  ownerComment: string;
  state: string;
  sentToExternalEmailAddress?: string;
  externalEmailAddress?: string;
  ownerEmails?: OwnerCaretakerCleanerEmail[];
  caretakerEmails?: OwnerCaretakerCleanerEmail[];
  cleanerEmails?: OwnerCaretakerCleanerEmail[];
  hasCaretaker: boolean;
  hasCleaner: boolean;
  ownerEmailFreeText?: string | null;
  maintainerEmailFreeText?: string | null;
  cleanerEmailFreeText?: string | null;
  externalEmailFreeText?: string | null;
  feedbackHandlerFirstName: string | null;
  feedbackHandlerLastName: string | null;
  autoApprove?: boolean;
  autoApproveReason?: string;
  sendEmailToCleaner?: boolean;
  sendEmailToCaretaker?: boolean;
}

export interface FeedbackListItem {
  id: string;
  sentTime: Date;
  answerTime: Date;
  feedbackState: string;
  contactRequested: boolean;
  customerFirstName: string;
  customerLastName: string;
  customerNumber: string;
  accommodationNumber: string;
  bookingStartTime: Date;
  bookingEndTime: Date;
  bookingNumber: string;
  ownerFirstName: string;
  ownerLastName: string;
  ownerNumber: string;
  siteId: string;
  locale: string;
  approvedRejectedTime: Date;
  ownerReplySentTime: Date;
  caretakerReplySentTime: Date;
  feedbackHandlerFirstName: string;
  feedbackHandlerLastName: string;
  autoApprove?: boolean;
  autoApproveReason?: string;
}

export interface FeedbacksState {
  feedbackItems: FeedbackListItem[];
  feedbackDetails: FeedbackDetails;
  feedbackItemsStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;
  feedbackDetailsStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;
  feedbackPagination: PaginationResponse;
  pendingFeedbacksCount: number;
  pendingFeedbacksCountStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;
}

const initialState: FeedbacksState = {
  feedbackItems: [],
  feedbackDetails: {
    id: '',
    isDeleted: false,
    sentTime: formatDateToFinnish(new Date()),
    answerTime: formatDateToFinnish(new Date()),
    dateOfPublication: '',
    feedbackState: '',
    contactRequested: false,
    customerFirstName: '',
    customerLastName: '',
    customerNumber: '',
    customerEmail: '',
    accommodationNumber: '',
    accommodationName: '',
    accommodationImageUrl: '',
    bookingStartTime: formatDateToFinnish(new Date()),
    bookingEndTime: formatDateToFinnish(new Date()),
    bookingNumber: '',
    ownerFirstName: '',
    ownerLastName: '',
    ownerNumber: '',
    siteId: '',
    locale: '',
    approvedRejectedTime: null,
    sentToOwner: null,
    sentToMaintainer: null,
    sentToCleaner: null,
    answers: [],
    serviceComment: '',
    ownerComment: '',
    state: 'InProgress',
    ownerEmails: [],
    caretakerEmails: [],
    cleanerEmails: [],
    hasCaretaker: false,
    hasCleaner: false,
    feedbackHandlerFirstName: '',
    feedbackHandlerLastName: '',
    sendEmailToCleaner: undefined,
    sendEmailToCaretaker: undefined,
    ownerEmailFreeText: '',
    maintainerEmailFreeText: '',
    cleanerEmailFreeText: '',
    externalEmailFreeText: '',
  },
  feedbackItemsStatus: LOADING_STATUS.IDLE,
  feedbackDetailsStatus: LOADING_STATUS.IDLE,
  feedbackPagination: InitialPaginationResponse,
  pendingFeedbacksCount: 0,
  pendingFeedbacksCountStatus: LOADING_STATUS.IDLE,
};

export const getFeedbacksAsync = createAsyncThunk(
  'customerfeedbacks/getCustomerFeedbacks',
  async (feedbacksFilter: FeedbacksFilter, { rejectWithValue }) => {
    try {
      const response = await fetchFeedbacks(feedbacksFilter);
      return response;
    } catch (error) {
      return rejectWithValue(error.response);
    }
  },
);

export const getFeedbackDetailsAsync = createAsyncThunk(
  'customerfeedbacks/getFeedbackDetails',
  async (feedbackId: string, { rejectWithValue }) => {
    try {
      const response = await fetchFeedbackDetails(feedbackId);
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const getFeedbackDetailsAsPdfAsync = createAsyncThunk(
  'customerfeedbacks/getFeedbackDetailsAsPdf',
  async (feedbackId: string, { rejectWithValue }) => {
    try {
      const pdfBytes = await fetchFeedbackDetailsAsPdf(feedbackId);
      return pdfBytes;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const sendFeedbackToOwner = createAsyncThunk(
  'customerfeedbacks/sendFeedbackToOwner',
  async (
    payload: OwnerCleanerCaretakerFeedbackEmailReplyDto,
    { rejectWithValue },
  ) => {
    try {
      const response = await doSendFeedbackToOwner(payload);
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const sendFeedbackToCaretaker = createAsyncThunk(
  'customerfeedbacks/sendFeedbackToCaretaker',
  async (
    payload: OwnerCleanerCaretakerFeedbackEmailReplyDto,
    { rejectWithValue },
  ) => {
    try {
      const response = await doSendFeedbackToCaretaker(payload);
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const sendFeedbackToCleaner = createAsyncThunk(
  'customerfeedbacks/sendFeedbackToCleaner',
  async (
    payload: OwnerCleanerCaretakerFeedbackEmailReplyDto,
    { rejectWithValue },
  ) => {
    try {
      const response = await doSendFeedbackToCleaner(payload);
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const sendFeedbackToExternal = createAsyncThunk(
  'customerfeedbacks/sendFeedbackToExternal',
  async (payload: ExternalEmailFeedbackEmailReplyDto, { rejectWithValue }) => {
    try {
      const response = await doSendFeedbackToExternalEmail(payload);
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const updateFeedbackAsync = createAsyncThunk(
  'customerfeedbacks/updateFeedback',
  async (feedbackData: FeedbackDetails, { rejectWithValue }) => {
    try {
      /*
      if (feedbackData.approvedRejectedTime != null) {
        feedbackData.approvedRejectedTime =
          convertJSDateToDotNetDateTimeWithTime(
            feedbackData.approvedRejectedTime,
          );
      }

       */
      const response = await updateFeedback(feedbackData);
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const updateFeedbackSentToOwnerAsync = createAsyncThunk(
  'customerfeedbacks/updateFeedbackSentToOwner',
  async (feedBackId: string, { rejectWithValue }) => {
    try {
      const response = await updateFeedbackSentToOwner(feedBackId);
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const updateFeedbackSentToMaintainerAsync = createAsyncThunk(
  'customerfeedbacks/updateFeedbackSentToMaintainer',
  async (feedBackId: string, { rejectWithValue }) => {
    try {
      const response = await updateFeedbackSentToMaintainer(feedBackId);
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const updateFeedbackSentToCleanerAsync = createAsyncThunk(
  'customerfeedbacks/updateFeedbackSentToCleaner',
  async (feedBackId: string, { rejectWithValue }) => {
    try {
      const response = await updateFeedbackSentToCleaner(feedBackId);
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const updateFeedbackSentToExternalEmailAddressAsync = createAsyncThunk(
  'customerfeedbacks/updateFeedbackSentToExternalEmailAddress',
  async (payload: ExternalEmailFeedbackEmailReplyDto, { rejectWithValue }) => {
    try {
      const response = await updateFeedbackSentToExternalEmailAddress(
        payload.feedbackId,
        payload.emailAddress,
      );
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const deleteFeedbackAsync = createAsyncThunk(
  'customerfeedbacks/deleteFeedback',
  async (feedbackId: string, { rejectWithValue }) => {
    try {
      const response = await deleteFeedback(feedbackId);
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const getPendingFeedbacksCountAsync = createAsyncThunk(
  'customerfeedbacks/getPendingFeedbacksCount',
  async (_, { rejectWithValue }) => {
    try {
      const response = await fetchPendingFeedbacksCount();
      return response;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

const feedbacksSlice = createSlice({
  name: 'feedbacks',
  initialState,
  reducers: {
    changeStateAnswerApproved: (state, action) => {
      const { answer, approved } = action.payload;
      const feedbackAnswer = state.feedbackDetails.answers.find(
        newAnswer => newAnswer.id === answer.id,
      );
      feedbackAnswer.approved = approved;

      feedbackAnswer.stateModified = convertJsDateToApiDateFinnishTimeString(
        new Date(),
      );
      // feedbackAnswer.stateModified = new Date();
    },
    changeStateAnswerRating: (state, action) => {
      const { answer, ratingRangeValue } = action.payload;
      const feedbackAnswer = state.feedbackDetails.answers.find(
        newAnswer => newAnswer.id === answer.id,
      );
      if (feedbackAnswer.originalNumbericValue == null) {
        feedbackAnswer.originalNumbericValue = feedbackAnswer.numbericValue;
      }
      feedbackAnswer.numbericValue = ratingRangeValue;
    },
    changeStateAnswerText: (state, action) => {
      const { answer, textValue } = action.payload;
      const feedbackAnswer = state.feedbackDetails.answers.find(
        newAnswer => newAnswer.id === answer.id,
      );
      if (feedbackAnswer.originalTextValue == null) {
        feedbackAnswer.originalTextValue = feedbackAnswer.textValue;
      }
      feedbackAnswer.textValue = textValue;
    },
    changeStateAnswerReply: (state, action) => {
      const { answer, textValue } = action.payload;
      const feedbackAnswer = state.feedbackDetails.answers.find(
        newAnswer => newAnswer.id === answer.id,
      );
      feedbackAnswer.reply = textValue;
    },
    changeStateAnswerSelection: (state, action) => {
      const { answer, selectionValue } = action.payload;
      const feedbackAnswer = state.feedbackDetails.answers.find(
        newAnswer => newAnswer.id === answer.id,
      );
      if (feedbackAnswer.originalNumbericValue == null) {
        feedbackAnswer.originalNumbericValue = feedbackAnswer.numbericValue;
      }
      feedbackAnswer.numbericValue = selectionValue;
    },
    changeStateFeedbackDateOfPublication: (state, action) => {
      const { dateOfPublicationValue } = action.payload;
      state.feedbackDetails.dateOfPublication = dateOfPublicationValue;
    },
    changeStateFeedbackServiceComment: (state, action) => {
      const { serviceComment } = action.payload;
      state.feedbackDetails.serviceComment = serviceComment;
    },
    changeStateFeedbackOwnerComment: (state, action) => {
      const { ownerComment } = action.payload;
      state.feedbackDetails.ownerComment = ownerComment;
    },
    changeStateFeedbackOwnerEmailFreeText: (state, action) => {
      const { ownerEmailFreeText } = action.payload;
      state.feedbackDetails.ownerEmailFreeText = ownerEmailFreeText;
    },
    changeStateFeedbackMaintainerEmailFreeText: (state, action) => {
      const { maintainerEmailFreeText } = action.payload;
      state.feedbackDetails.maintainerEmailFreeText = maintainerEmailFreeText;
    },
    changeStateFeedbackCleanerEmailFreeText: (state, action) => {
      const { cleanerEmailFreeText } = action.payload;
      state.feedbackDetails.cleanerEmailFreeText = cleanerEmailFreeText;
    },
    changeStateFeedbackExternalEmailFreeText: (state, action) => {
      const { externalEmailFreeText } = action.payload;
      state.feedbackDetails.externalEmailFreeText = externalEmailFreeText;
    },
    changeStateFeedbackState: (state, action) => {
      const { newFeedBackState } = action.payload;

      state.feedbackDetails.approvedRejectedTime =
        convertJsDateToApiDateFinnishTimeString(new Date());
      state.feedbackDetails.state = newFeedBackState;
    },
    changeStateFeedbackSentToOwner: state => {
      state.feedbackDetails.sentToOwner =
        convertJsDateToApiDateFinnishTimeString(new Date());
    },
    changeStateFeedbackSentToMaintainer: state => {
      state.feedbackDetails.sentToMaintainer =
        convertJsDateToApiDateFinnishTimeString(new Date());
    },
    changeStateFeedbackSentToCleaner: state => {
      state.feedbackDetails.sentToCleaner =
        convertJsDateToApiDateFinnishTimeString(new Date());
    },
    changeStateFeedbackSentToExternalEmailAddress: (state, action) => {
      state.feedbackDetails.sentToExternalEmailAddress =
        convertJsDateToApiDateFinnishTimeString(new Date());
      state.feedbackDetails.externalEmailAddress = action.payload;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(getFeedbacksAsync.pending, state => {
        state.feedbackItemsStatus = LOADING_STATUS.LOADING;
      })
      .addCase(getFeedbacksAsync.fulfilled, (state, action) => {
        state.feedbackItemsStatus = LOADING_STATUS.IDLE;
        state.feedbackItems = action.payload.data;
        state.feedbackPagination = PopulatePagination(action.payload);
      })
      .addCase(getFeedbacksAsync.rejected, state => {
        state.feedbackItemsStatus = LOADING_STATUS.FAILED;
      })
      .addCase(getFeedbackDetailsAsync.pending, state => {
        state.feedbackDetailsStatus = LOADING_STATUS.LOADING;
      })
      .addCase(getFeedbackDetailsAsync.fulfilled, (state, action) => {
        state.feedbackDetailsStatus = LOADING_STATUS.IDLE;
        const apiData = action.payload as FeedbackDetails;
        if (apiData) {
          state.feedbackDetails = {
            ...apiData,
            ownerEmailFreeText: apiData?.ownerEmailFreeText ?? '',
            maintainerEmailFreeText: apiData?.maintainerEmailFreeText ?? '',
            cleanerEmailFreeText: apiData?.cleanerEmailFreeText ?? '',
            externalEmailFreeText: apiData?.externalEmailFreeText ?? '',
          };
        }
      })
      .addCase(getFeedbackDetailsAsync.rejected, state => {
        state.feedbackDetailsStatus = LOADING_STATUS.FAILED;
      })
      .addCase(getPendingFeedbacksCountAsync.pending, state => {
        state.pendingFeedbacksCountStatus = LOADING_STATUS.LOADING;
      })
      .addCase(getPendingFeedbacksCountAsync.fulfilled, (state, action) => {
        state.pendingFeedbacksCountStatus = LOADING_STATUS.IDLE;
        state.pendingFeedbacksCount = action.payload;
      })
      .addCase(getPendingFeedbacksCountAsync.rejected, state => {
        state.pendingFeedbacksCountStatus = LOADING_STATUS.FAILED;
      });
  },
});

export const selectFeedbackItems = (state: RootState) =>
  state.feedbacks.feedbackItems;
export const selectFeedbackPagination = (state: RootState) =>
  state.feedbacks.feedbackPagination;
export const selectFeedbackItemsLoadingStatus = (state: RootState) =>
  state.feedbacks.feedbackItemsStatus;
export const selectFeedbackDetails = (state: RootState) =>
  state.feedbacks.feedbackDetails;
export const selectFeedbackDetailsLoadingStatus = (state: RootState) =>
  state.feedbacks.feedbackDetailsStatus;
export const selectPendingFeedbacksCount = (state: RootState) =>
  state.feedbacks.pendingFeedbacksCount;
export const selectPendingFeedbacksCountLoadingStatus = (state: RootState) =>
  state.feedbacks.pendingFeedbacksCountStatus;
export const { changeStateAnswerApproved } = feedbacksSlice.actions;
export const { changeStateAnswerRating } = feedbacksSlice.actions;
export const { changeStateAnswerText } = feedbacksSlice.actions;
export const { changeStateAnswerReply } = feedbacksSlice.actions;
export const { changeStateAnswerSelection } = feedbacksSlice.actions;
export const { changeStateFeedbackDateOfPublication } = feedbacksSlice.actions;
export const { changeStateFeedbackServiceComment } = feedbacksSlice.actions;
export const { changeStateFeedbackOwnerComment } = feedbacksSlice.actions;
export const { changeStateFeedbackState } = feedbacksSlice.actions;
export const { changeStateFeedbackSentToOwner } = feedbacksSlice.actions;
export const { changeStateFeedbackSentToMaintainer } = feedbacksSlice.actions;
export const { changeStateFeedbackSentToCleaner } = feedbacksSlice.actions;
export const { changeStateFeedbackSentToExternalEmailAddress } =
  feedbacksSlice.actions;
export const { changeStateFeedbackOwnerEmailFreeText } = feedbacksSlice.actions;
export const { changeStateFeedbackMaintainerEmailFreeText } =
  feedbacksSlice.actions;
export const { changeStateFeedbackCleanerEmailFreeText } =
  feedbacksSlice.actions;
export const { changeStateFeedbackExternalEmailFreeText } =
  feedbacksSlice.actions;

export default feedbacksSlice.reducer;
